import {
  useEffect,
  useMemo,
  type KeyboardEvent,
  type MouseEvent,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  geoGraticule10,
  geoNaturalEarth1,
  geoPath,
  type GeoProjection,
} from 'd3-geo';
import { Icon } from '@iconify/react';
import { feature, mesh } from 'topojson-client';
import type { Topology, GeometryCollection } from 'topojson-specification';
import type { FeatureCollection, MultiLineString, Geometry } from 'geojson';
import {
  centerLongitude,
  voyageMatches,
  type ArrivalMap,
  type LngLat,
  type MapFilter,
  type MapTone,
  type MapVoyage,
} from './map-model';

// Theme tokens, so the map follows light and dark mode.
const TONE: Record<MapTone, string> = {
  danger: 'hsl(var(--nextui-danger))',
  warning: 'hsl(var(--nextui-warning))',
  success: 'hsl(var(--nextui-success))',
};
const INK = 'hsl(var(--nextui-foreground))';
const MUTED = 'hsl(var(--nextui-default-500))';
const HALO = 'hsl(var(--nextui-content1))';
const SEA = 'hsl(var(--nextui-default-50))';
const LAND = 'hsl(var(--nextui-default-200))';
const BORDER = 'hsl(var(--nextui-default-300))';
const GRID = 'hsl(var(--nextui-default-100))';

interface World {
  land: FeatureCollection<Geometry>;
  borders: MultiLineString;
}
let worldPromise: Promise<World> | null = null;
// The atlas (~100 KB) loads only when a map is shown.
const loadWorld = () =>
  (worldPromise ??= import('world-atlas/countries-110m.json').then((module) => {
    const topo = (module.default ?? module) as unknown as Topology<{
      countries: GeometryCollection;
      land: GeometryCollection;
    }>;
    return {
      land: feature(topo, topo.objects.land) as FeatureCollection<Geometry>,
      borders: mesh(
        topo,
        topo.objects.countries,
        (a, b) => a !== b
      ) as MultiLineString,
    };
  }));

type Box = { x0: number; x1: number; y0: number; y1: number };
const overlaps = (a: Box, b: Box) =>
  a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
const fx = (n: number) => n.toFixed(1);

interface MapHover {
  title: string;
  detail?: string;
}
/** Action of a click on the empty map (e.g. open it full screen). */
export interface MapBackgroundAction {
  label: string;
  icon: string;
  onPress: () => void;
}

/** Frame the projection on what is drawn, keeping some world around it. */
export function frame(map: ArrivalMap, width: number, height: number) {
  const points: LngLat[] = [
    ...map.origins.map((o) => o.coordinates),
    ...map.destinations.flatMap((d) => (d.coordinates ? [d.coordinates] : [])),
    ...map.voyages.flatMap((v) => (v.position ? [v.position] : [])),
    ...map.voyages.flatMap((v) =>
      v.segments.flatMap((s) => s.coordinates.filter((_, i) => i % 8 === 0))
    ),
  ];
  const projection = geoNaturalEarth1().rotate([
    -centerLongitude(points.length ? points : [[-70, -33]]),
    0,
  ]);
  const small = width < 500;
  const padX = Math.min(70, width * (small ? 0.1 : 0.08));
  const padY = Math.min(44, height * 0.12);
  const target = {
    type: 'MultiPoint' as const,
    coordinates: points.length
      ? points
      : [
          [-80, -50],
          [140, 50],
        ],
  };
  projection.fitExtent(
    [
      [padX, padY],
      // Bottom margin keeps the legend clear of the southernmost marks.
      [width - padX, height - padY - (small ? 40 : 12)],
    ],
    target
  );
  // At most ~140° of longitude or ~100° of latitude across the frame.
  const maxScale = Math.min(width / 2.2, height / 1.5);
  if (projection.scale() > maxScale) {
    // A single point (or all in one place) fits to an infinite scale and
    // translate, so start the recentering from the middle of the frame.
    projection.scale(maxScale).translate([width / 2, height / 2]);
    const [[x0, y0], [x1, y1]] = geoPath(projection).bounds(target);
    const [tx, ty] = projection.translate();
    projection.translate([
      tx + width / 2 - (x0 + x1) / 2,
      ty + height / 2 - (y0 + y1) / 2,
    ]);
  }
  return projection;
}

/** Split a live segment at the vessel: done (solid) and still to go (dashed). */
function splitAt(coordinates: LngLat[], position: LngLat) {
  let nearest = 0;
  let best = Infinity;
  coordinates.forEach(([lng, lat], index) => {
    const distance = (lng - position[0]) ** 2 + (lat - position[1]) ** 2;
    if (distance < best) {
      best = distance;
      nearest = index;
    }
  });
  return {
    done: [...coordinates.slice(0, nearest + 1), position],
    ahead: [position, ...coordinates.slice(nearest + 1)],
  };
}

// Route stroke: thicker and fully opaque while its voyage is hovered (CSS, so
// hovering never re-renders the map). A wide invisible stroke eases hovering.
const LINE_CLASS =
  'transition-[stroke-width,stroke-opacity] duration-150 [stroke-width:var(--w)] group-hover/voyage:[stroke-width:var(--wh)] group-hover/voyage:[stroke-opacity:1]';
const RouteLine = ({
  d,
  color,
  width,
  hoverWidth,
  opacity,
  dash,
  active,
}: {
  d: string;
  color: string;
  width: number;
  hoverWidth: number;
  opacity: number;
  dash?: string;
  active: boolean;
}) => (
  <>
    <path d={d} fill='none' stroke='transparent' strokeWidth={12} />
    <path
      d={d}
      fill='none'
      stroke={color}
      strokeOpacity={active ? 1 : opacity}
      strokeDasharray={dash}
      strokeLinecap='round'
      className={LINE_CLASS}
      style={
        {
          '--w': `${active ? hoverWidth : width}px`,
          '--wh': `${hoverWidth}px`,
        } as CSSProperties
      }
    />
  </>
);

function VoyageMarks({
  voyage,
  projection,
  small,
  active,
}: {
  voyage: MapVoyage;
  projection: GeoProjection;
  small: boolean;
  active: boolean;
}) {
  const path = geoPath(projection);
  const color = TONE[voyage.tone];
  const line = (coordinates: LngLat[], dashed: boolean, key: string) => {
    const d = path({ type: 'LineString', coordinates });
    return d ? (
      <RouteLine
        key={key}
        d={d}
        color={color}
        width={dashed ? (small ? 1.8 : 2.2) : small ? 2.4 : 3}
        hoverWidth={dashed ? 3.6 : 4.5}
        opacity={dashed ? 0.6 : 0.85}
        dash={dashed ? '3 5' : undefined}
        active={active}
      />
    ) : null;
  };
  const marks: JSX.Element[] = [];
  let position: [number, number] | null = null;
  let estimated = false;

  if (voyage.segments.length || voyage.position) {
    voyage.segments.forEach((segment, index) => {
      if (segment.status === 'CURRENT' && voyage.position) {
        const { done, ahead } = splitAt(segment.coordinates, voyage.position);
        marks.push(line(done, false, `${index}-done`)!);
        marks.push(line(ahead, true, `${index}-ahead`)!);
      } else
        marks.push(
          line(segment.coordinates, segment.status === 'FUTURE', String(index))!
        );
    });
    voyage.stops.forEach((stop, index) => {
      const at = projection(stop);
      if (at)
        marks.push(
          <circle
            key={`stop-${index}`}
            cx={at[0]}
            cy={at[1]}
            r={2.5}
            fill={HALO}
            stroke={color}
            strokeWidth={1.2}
          />
        );
    });
    position = voyage.position ? projection(voyage.position) : null;
  } else if (voyage.origin && voyage.destination) {
    // No provider route yet: a plain arc, and a position estimated from the
    // ETD/ETA, as in the reference design.
    const o = projection(voyage.origin);
    const s = projection(voyage.destination);
    if (o && s) {
      const dx = s[0] - o[0];
      const dy = s[1] - o[1];
      const length = Math.hypot(dx, dy) || 1;
      let nx = -dy / length;
      let ny = dx / length;
      if (ny > 0) {
        nx = -nx;
        ny = -ny;
      }
      const c = [
        (o[0] + s[0]) / 2 + nx * length * 0.22,
        (o[1] + s[1]) / 2 + ny * length * 0.22,
      ];
      marks.push(
        <RouteLine
          key='arc'
          d={`M${fx(o[0])},${fx(o[1])} Q${fx(c[0])},${fx(c[1])} ${fx(s[0])},${fx(s[1])}`}
          color={color}
          width={small ? 1.8 : 2.2}
          hoverWidth={3.6}
          opacity={0.55}
          dash='1 6'
          active={active}
        />
      );
      if (voyage.progress !== null && voyage.progress < 1) {
        const t = voyage.progress;
        position = [
          (1 - t) ** 2 * o[0] + 2 * (1 - t) * t * c[0] + t * t * s[0],
          (1 - t) ** 2 * o[1] + 2 * (1 - t) * t * c[1] + t * t * s[1],
        ];
        estimated = true;
      }
    }
  }

  if (position) {
    const [x, y] = position;
    const r = small ? 3.5 : 4.5;
    marks.push(
      <g key='position'>
        {!estimated && (
          <circle cx={x} cy={y} r={r + 4} fill={color} fillOpacity={0.2} />
        )}
        <circle
          cx={x}
          cy={y}
          r={r}
          fill={estimated ? HALO : color}
          stroke={estimated ? color : HALO}
          strokeWidth={estimated ? 1.6 : 2}
        />
        <circle cx={x} cy={y} r={r + 8} fill='transparent' />
      </g>
    );
  }
  return <>{marks}</>;
}

export default function ArrivalsMap({
  map,
  filter,
  onFilter,
  backgroundAction,
  height = 400,
  fill = false,
}: {
  map: ArrivalMap;
  filter: MapFilter | null;
  onFilter: (filter: MapFilter | null) => void;
  backgroundAction?: MapBackgroundAction;
  height?: number;
  fill?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [world, setWorld] = useState<World | null>(null);
  const [failed, setFailed] = useState(false);
  // Hover: what is under the pointer (route, port, origin) or, on the empty
  // map, the background action, shown next to the pointer.
  const [hover, setHover] = useState<MapHover | null>(null);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    let active = true;
    loadWorld()
      .then((value) => active && setWorld(value))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) =>
      setSize({
        width: Math.round(entry.contentRect.width),
        height: Math.round(entry.contentRect.height),
      })
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const width = size.width;
  const mapHeight = fill
    ? size.height || height
    : width
      ? Math.min(height, Math.max(300, Math.round(width * 0.62)))
      : height;
  const small = width < 500;
  const projection = useMemo(
    () => (width ? frame(map, width, mapHeight) : null),
    [map, width, mapHeight]
  );

  // The inline map has the "Ampliar" button and the legend drawn over it.
  const overlaid = Boolean(backgroundAction);
  const content = useMemo(() => {
    if (!world || !projection) return null;
    const path = geoPath(projection);
    const pick = (next: MapFilter) =>
      onFilter(
        filter && filter.kind === next.kind && filter.key === next.key
          ? null
          : next
      );
    const matching = filter
      ? map.voyages.filter((voyage) => voyageMatches(voyage, filter))
      : map.voyages;
    const litPods = new Set(matching.map((voyage) => voyage.pod));
    if (filter?.kind === 'pod') litPods.add(filter.key);
    const litOrigins = new Set(matching.map((voyage) => voyage.originKey));
    const interactive = (
      title: string,
      detail: string,
      onPress: () => void
    ) => ({
      'data-interactive': true,
      role: 'button',
      tabIndex: 0,
      'aria-label': `${title}. ${detail}`,
      className: 'cursor-pointer outline-none transition-opacity',
      onMouseEnter: () => setHover({ title, detail }),
      onMouseLeave: () => setHover(null),
      onFocus: () => setHover({ title, detail }),
      onBlur: () => setHover(null),
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        setHover(null);
        onPress();
      },
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onPress();
        }
      },
    });
    const items = map.destinations.flatMap((destination) => {
      const at = destination.coordinates
        ? projection(destination.coordinates)
        : null;
      if (!at) return [];
      const r =
        (small ? 5 : 7) +
        Math.sqrt(destination.kg / 1000) * (small ? 1.1 : 1.6);
      return [{ destination, at, r: Math.min(r, small ? 18 : 26) }];
    });

    // Labels: try beside, above and below each port and pick the closest
    // free spot, starting with the most crowded ports.
    const taken: Box[] = items.map(({ at, r }) => ({
      x0: at[0] - r,
      x1: at[0] + r,
      y0: at[1] - r,
      y1: at[1] + r,
    }));
    // Keep labels out of the "Ampliar" button (top right) and the legend
    // (bottom left) drawn over the map.
    if (overlaid)
      taken.push(
        { x0: width - 120, x1: width, y0: 0, y1: 48 },
        { x0: 0, x1: small ? width : 420, y0: mapHeight - 40, y1: mapHeight }
      );
    const originPoints = map.origins.flatMap((origin) => {
      const at = projection(origin.coordinates);
      return at ? [{ ...origin, at }] : [];
    });
    for (const origin of originPoints)
      taken.push({
        x0: origin.at[0] - 60,
        x1: origin.at[0] + 60,
        y0: origin.at[1] - 8,
        y1: origin.at[1] + 28,
      });
    const crowd = (item: (typeof items)[number]) =>
      items.filter(
        (other) =>
          other !== item &&
          Math.hypot(other.at[0] - item.at[0], other.at[1] - item.at[1]) < 160
      ).length;
    const sub = (d: (typeof items)[number]['destination']) => {
      const units = new Set(d.arrivals.map((a) => a.containerKey)).size;
      const tons = (d.kg / 1000).toLocaleString('es-CL', {
        maximumFractionDigits: 1,
      });
      return `${units} ${units === 1 ? 'unidad' : 'unidades'} · ${tons} t`;
    };
    const labels = new Map<
      string,
      {
        x: number;
        y: number;
        anchor: 'start' | 'end' | 'middle';
        moved: boolean;
        box: Box;
      }
    >();
    [...items]
      .sort((a, b) => crowd(b) - crowd(a))
      .forEach((item) => {
        const { at, r, destination } = item;
        const textWidth =
          Math.max(
            destination.name.length * (small ? 6.6 : 7.4),
            small ? 0 : sub(destination).length * 6.2
          ) + 4;
        const boxHeight = small ? 14 : 30;
        const candidates: Array<{
          x: number;
          y: number;
          anchor: 'start' | 'end' | 'middle';
          moved: boolean;
          box: Box;
          cost: number;
        }> = [];
        const add = (
          anchor: 'start' | 'end' | 'middle',
          x: number,
          y: number,
          moved: boolean
        ) => {
          const x0 =
            anchor === 'end'
              ? x - textWidth
              : anchor === 'middle'
                ? x - textWidth / 2
                : x;
          const box = {
            x0,
            x1: x0 + textWidth,
            y0: y - 10,
            y1: y - 10 + boxHeight,
          };
          candidates.push({
            anchor,
            x,
            y,
            moved,
            box,
            cost:
              Math.hypot(
                (box.x0 + box.x1) / 2 - at[0],
                (box.y0 + box.y1) / 2 - at[1]
              ) + (moved ? 40 : 0),
          });
        };
        const middle = at[1] - (small ? 4 : 10) + 4;
        add('start', at[0] + r + 6, middle, false);
        add('end', at[0] - r - 6, middle, false);
        add('middle', at[0], at[1] - r - boxHeight + 4, false);
        add('middle', at[0], at[1] + r + 14, false);
        for (const step of [1, 2, 3])
          for (const sign of [-1, 1]) {
            const dy = sign * step * (boxHeight + 6);
            add('start', at[0] + r + 14, middle + dy, true);
            add('end', at[0] - r - 14, middle + dy, true);
            add(
              'middle',
              at[0],
              (sign < 0 ? at[1] - r - boxHeight + 4 : at[1] + r + 14) + dy,
              true
            );
          }
        const inside = (c: (typeof candidates)[number]) =>
          c.box.x0 > 2 &&
          c.box.x1 < width - 2 &&
          c.box.y0 > 2 &&
          c.box.y1 < mapHeight - 2;
        const free = (c: (typeof candidates)[number]) =>
          inside(c) && !taken.some((b) => overlaps(b, c.box));
        const byCost = (a: { cost: number }, b: { cost: number }) =>
          a.cost - b.cost;
        const pick =
          candidates.filter(free).sort(byCost)[0] ??
          candidates.filter(inside).sort(byCost)[0] ??
          candidates[0];
        taken.push(pick.box);
        labels.set(destination.pod, pick);
      });

    const halo = {
      paintOrder: 'stroke' as const,
      stroke: HALO,
      strokeWidth: 4,
      strokeLinejoin: 'round' as const,
    };
    return (
      <>
        <path d={path(geoGraticule10()) ?? ''} fill='none' stroke={GRID} />
        <path d={path(world.land) ?? ''} fill={LAND} />
        <path
          d={path(world.borders) ?? ''}
          fill='none'
          stroke={BORDER}
          strokeWidth={0.6}
        />
        {map.voyages.map((voyage) => {
          const lit = !filter || voyageMatches(voyage, filter);
          const position =
            voyage.progress !== null
              ? 'Posición estimada según ETD y ETA. '
              : voyage.position
                ? 'Posición informada por el seguimiento. '
                : '';
          const props = interactive(
            voyage.routeLabel,
            `${voyage.label}. ${position}Clic para ver los envíos de esta ruta.`,
            () =>
              pick({
                kind: 'route',
                key: voyage.routeKey,
                label: voyage.routeLabel,
              })
          );
          return (
            <g
              key={voyage.key}
              {...props}
              opacity={lit ? 1 : 0.15}
              className={`group/voyage ${props.className}`}
            >
              <VoyageMarks
                voyage={voyage}
                projection={projection}
                small={small}
                active={filter?.kind === 'route' && lit}
              />
            </g>
          );
        })}
        {items.map(({ destination, at, r }) => {
          const label = labels.get(destination.pod)!;
          const color = TONE[destination.tone];
          const isSelected =
            filter?.kind === 'pod' && filter.key === destination.pod;
          return (
            <g
              key={destination.pod}
              {...interactive(
                `${destination.flag} ${destination.name}`.trim(),
                `${sub(destination)}. Clic para filtrar este destino.`,
                () =>
                  pick({
                    kind: 'pod',
                    key: destination.pod,
                    label: destination.name,
                  })
              )}
              aria-pressed={isSelected}
              opacity={!filter || litPods.has(destination.pod) ? 1 : 0.25}
            >
              <circle
                cx={at[0]}
                cy={at[1]}
                r={r}
                fill={color}
                fillOpacity={0.18}
                stroke={color}
                strokeWidth={isSelected ? 2.6 : 1.2}
                className='transition-[stroke-width,fill-opacity] duration-150 hover:[fill-opacity:0.35] hover:[stroke-width:2.6px]'
              />
              <circle cx={at[0]} cy={at[1]} r={3} fill={color} />
              {label.moved && (
                <line
                  x1={at[0]}
                  y1={at[1]}
                  x2={Math.min(Math.max(at[0], label.box.x0), label.box.x1)}
                  y2={label.box.y1 < at[1] ? label.box.y1 : label.box.y0}
                  stroke={color}
                  strokeOpacity={0.6}
                />
              )}
              <text
                x={label.x}
                y={label.y}
                textAnchor={label.anchor}
                fill={INK}
                fontSize={small ? 11 : 12}
                fontWeight={700}
                {...halo}
              >
                {destination.name}
              </text>
              {!small && (
                <text
                  x={label.x}
                  y={label.y + 15}
                  textAnchor={label.anchor}
                  fill={MUTED}
                  fontSize={11}
                  {...halo}
                >
                  {sub(destination)}
                </text>
              )}
            </g>
          );
        })}
        {originPoints.map((origin) => {
          const text = small ? origin.name : `${origin.name} · origen`;
          const x = Math.min(
            Math.max(origin.at[0], text.length * 3.4 + 4),
            width - text.length * 3.4 - 4
          );
          const isSelected =
            filter?.kind === 'origin' && filter.key === origin.key;
          const count = map.voyages.filter(
            (voyage) => voyage.originKey === origin.key
          ).length;
          return (
            <g
              key={origin.key}
              {...interactive(
                `${origin.name} · origen`,
                `${count} ${count === 1 ? 'envío' : 'envíos'} en ventana. Clic para filtrar este origen.`,
                () =>
                  pick({
                    kind: 'origin',
                    key: origin.key,
                    label: `Desde ${origin.name}`,
                  })
              )}
              aria-pressed={isSelected}
              opacity={!filter || litOrigins.has(origin.key) ? 1 : 0.3}
              className='group/origin cursor-pointer outline-none transition-opacity'
            >
              <circle
                cx={origin.at[0]}
                cy={origin.at[1]}
                r={11}
                fill={INK}
                className={`transition-opacity duration-150 group-hover/origin:opacity-20 ${isSelected ? 'opacity-20' : 'opacity-0'}`}
              />
              <rect
                x={origin.at[0] - 4.5}
                y={origin.at[1] - 4.5}
                width={9}
                height={9}
                fill={INK}
                transform={`rotate(45 ${origin.at[0]} ${origin.at[1]})`}
              />
              <text
                x={x}
                // Above the mark when the legend would cover the label.
                y={
                  origin.at[1] + 21 > mapHeight - 44
                    ? origin.at[1] - 12
                    : origin.at[1] + 21
                }
                textAnchor='middle'
                fill={INK}
                fontSize={11}
                fontWeight={600}
                {...halo}
              >
                {text}
              </text>
            </g>
          );
        })}
      </>
    );
  }, [
    world,
    projection,
    map,
    filter,
    small,
    width,
    mapHeight,
    onFilter,
    overlaid,
  ]);

  const track = (event: MouseEvent) => {
    const box = ref.current?.getBoundingClientRect();
    if (box)
      setPointer({ x: event.clientX - box.left, y: event.clientY - box.top });
  };
  const onEmpty = pointer && !hover && backgroundAction;

  return (
    <div
      ref={ref}
      className='relative w-full overflow-hidden'
      style={{ height: fill ? '100%' : mapHeight, background: SEA }}
    >
      {content && width > 0 ? (
        <svg
          width={width}
          height={mapHeight}
          className={
            backgroundAction && !hover ? 'block cursor-zoom-in' : 'block'
          }
          aria-label='Mapa de rutas y destinos en la ventana de arribos'
          onMouseMove={track}
          onMouseLeave={() => {
            setPointer(null);
            setHover(null);
          }}
          onClick={() => backgroundAction?.onPress()}
        >
          {content}
        </svg>
      ) : (
        <div className='absolute inset-0 flex items-center justify-center text-xs text-default-500'>
          {failed ? 'No se pudo cargar el mapa' : 'Cargando mapa…'}
        </div>
      )}
      {hover && pointer && (
        <div
          className='pointer-events-none absolute z-10 max-w-[260px] rounded-lg bg-content1 px-3 py-2 text-xs shadow-medium'
          style={{
            left: Math.min(pointer.x + 14, Math.max(0, width - 270)),
            top: Math.max(4, pointer.y - 64),
          }}
        >
          <p className='font-semibold text-foreground'>{hover.title}</p>
          {hover.detail && (
            <p className='mt-0.5 text-default-500'>{hover.detail}</p>
          )}
        </div>
      )}
      {onEmpty && (
        <div
          className='pointer-events-none absolute z-10 flex items-center gap-1.5 whitespace-nowrap rounded-full bg-content1/95 px-2.5 py-1 text-[11px] font-semibold text-foreground shadow-medium'
          style={{
            left: Math.min(pointer.x + 14, Math.max(0, width - 150)),
            top: Math.max(4, pointer.y + 14),
          }}
        >
          <Icon icon={backgroundAction.icon} width={13} />
          {backgroundAction.label}
        </div>
      )}
    </div>
  );
}
